<script setup lang="ts">
import consola from 'consola'
import { emitter } from './utils/mitt'

const { $mitt } = useNuxtApp()
const { formatQuery } = useUseBeforeRequest()

const categories = useState<_CategoryStateItem[]>('Categories', () => [])

/** 节日 */
const holidayStore = useState('_HolidayStore', () => [])

await useAsyncData('ichi_Categories_store', () =>
  $fetch<_ResponseType<_Pagination<_CategoryStateItem[]>>>(
    '/api/category/list',
    {
      method: 'POST',
      params: {
        size: 999999,
        publishStatus: 1,
      },
      onResponse({ response }) {
        const { code, data } = response._data
        if (code === 200) {
          categories.value = data.records
        }
      },
    },
  ))

await useAsyncData<_ResponseType<_Pagination<any>>>('holidays', () => $fetch('/api/holidayAttribute/listForCustomer', {
  method: 'POST',
  params: {
    size: 999999,
  },
  retry: 2,
  retryDelay: 1000,
  onResponse({ response }) {
    const { code, data } = response._data
    if (code === 200) {
      holidayStore.value = data.records
    }
  },
}))

// 挂载 pinia
const authStore = useAuthStore()
const cart = useCartStore()

await cart.syncCart()
function handleIframeMessage(event: MessageEvent) {
  if (event.origin.includes('localhost') || event.origin.includes('192.168.') || event.origin.includes('admin.ichi-cake')) {
    const { data, type } = event.data
    if (type !== 'page-define')
      return

    emitter.emit('bannerChange', data)
  }
}

function handleClickA(event: MouseEvent) {
  event.preventDefault()
  event.stopPropagation()
  event.stopImmediatePropagation()
  const el = event.currentTarget as HTMLAnchorElement

  // 获取自定义 data 属性
  const eventType = el.getAttribute('data-event-type')
  if (eventType && eventType === 'page-define') {
    const eventId = el.getAttribute('data-event-id')
    const imgTypeId = el.getAttribute('data-type-id')

    // 向父窗口发送消息
    window.parent.postMessage({
      type: eventType,
      id: eventId,
      imgTypeId,
    }, '*')
  }
}

function handleClickImg(event: MouseEvent) {
  event.preventDefault()
  event.stopPropagation()
  event.stopImmediatePropagation()
  const el = event.currentTarget as HTMLImageElement

  // 获取自定义 data 属性
  let eventType = el.getAttribute('data-event-type')
  let eventId = el.getAttribute('data-event-id')
  let imgTypeId = el.getAttribute('data-type-id')
  if (!eventType) {
    // 如果图片没有 data-event-type 属性，则尝试获取父元素 a 标签的 data-event-type 属性
    const parentEl = el.parentElement
    if (parentEl && parentEl.tagName === 'A') {
      eventType = parentEl.getAttribute('data-event-type')
      eventId = parentEl.getAttribute('data-event-id')
      imgTypeId = parentEl.getAttribute('data-type-id')
    }
  }

  if (eventType && (eventType === 'page-define')) {
    window.parent.postMessage({
      type: eventType,
      id: eventId,
      imgTypeId,
    }, '*')
  }
}

/**
 * 会员续费确认
 */
function confirmRenew() {
  const userinfo = authStore.userinfo
  showDialog({
    title: '會員續會確認',
    allowHtml: true,
    message: `<div>尊敬的會員，是否確認花費 <span class="text-theme">${userinfo!.renewPoint}</span> 進行續會？</div>`,
    cancelButtonText: '關閉',
    confirmButtonText: '確認',
    showCancelButton: true,
    beforeClose(action: string) {
      if (action === 'cancel') {
        return true
      }
      if (action === 'confirm') {
        return new Promise((resolve) => {
          $fetch('/ichi_api/point/renew', {
            method: 'POST',
            params: {
              MESSAGE: formatQuery({ customerId: userinfo?.id, custCode: userinfo?.custCode, mbType: userinfo?.mbType }),
            },
            onResponse({ response }) {
              if (response._data.success) {
                // 刷新用戶信息
                authStore.getUserInfo().then(() => {
                  resolve(true)
                })
              }
              else {
                showDialog({
                  title: '會員續會通知',
                  message: response._data?.data?.msg || response._data?.msg || response._data?.message || response.statusText,
                  confirmButtonText: '關閉',
                })

                showNotify(response._data?.data?.msg || response._data?.msg || response._data?.message || response.statusText)
                resolve(false)
              }
            },
          })
        })
      }
    },
  }).then(() => {
    const user = useState<_JwtPayload | null>('user', () => null)
    showDialog({
      title: '會員續會通知',
      allowHtml: true,
      message: `<div>尊敬的會員，您的會員卡續會成功。會員有效期至 <span class="text-theme">${dayjs(user.value!.mbExpired).format('MM/DD/YYYY')}</span></div>`,
      confirmButtonText: '關閉',
    })
  })
}

/**
 * 显示将过期提示弹窗
 */
function showExpireTip() {
  sessionStorage.setItem('hasShownExpireTip', 'true')
  const userinfo = authStore.userinfo
  const expire = dayjs(userinfo?.mbExpired)
  showConfirmDialog({
    title: '會籍即將到期提示',
    allowHtml: true,
    message: `<div style="text-align: left">尊敬的會員，您的會員卡將在 <span style="color: red">${expire.format('YYYY/MM/DD')}</span> 到期，請及時續會。</br><span style="color: red">若會員卡到期，閣下之積分將會失效。</span></div>`,
    showCancelButton: true,
    cancelButtonText: '取消',
    confirmButtonText: '去續會',
  }).then(() => {
    confirmRenew()
  }).catch(() => {
    consola.info('取消')
  })
}

function checkExpireTip() {
  const userinfo = authStore.userinfo
  const expire = dayjs(userinfo?.mbExpired)
  const hasShownExpireTip = userinfo?.mbType === 'M' && expire.diff(dayjs(), 'day') < 30
  // M 會員到期日小於 30 天
  if (!sessionStorage.getItem('hasShownExpireTip') && hasShownExpireTip) {
    showExpireTip()
  }
}

onBeforeUnmount(() => {
  $mitt.off('logined')
})

onMounted(() => {
  window.addEventListener('message', handleIframeMessage, false)

  nextTick(() => {
    checkExpireTip()

    $mitt.on('logined', () => {
      checkExpireTip()
    })

    // 如果页面被包含
    if (isInsideIframe) {
      // 阻止所有路由跳转
      window.history.pushState = () => {}

      document.querySelectorAll('a').forEach((el) => {
        el.addEventListener('click', handleClickA)
      })

      document.querySelectorAll('img').forEach((el) => {
        el.addEventListener('click', handleClickImg)
      })
    }
  })
})

onUnmounted(() => {
  window.removeEventListener('message', handleIframeMessage, false)
  document.querySelectorAll('a').forEach((el) => {
    el.removeEventListener('click', handleClickA)
  })
})
</script>

<template>
  <NuxtLoadingIndicator color="#f3f3f3" :throttle="300" :duration="500" :height="2" />
  <NuxtRouteAnnouncer />
  <NuxtLayout>
    <NuxtPage />
  </NuxtLayout>
</template>

<style>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}

.fade-enter,
.fade-leave-active {
  opacity: 0;
}

/* 设置滚动条 */
::-webkit-scrollbar {
  width: 1px;
  height: 5px;
}
html,
body {
  /* ios 禁止橡皮筋效果 */
  overscroll-behavior-y: none;
  -webkit-overflow-scrolling: touch;
  /* 设置滚动条 */
  scrollbar-width: thin;
  scrollbar-color: #d9d9d9 transparent;
}

/* 滚动条滑块 */
::-webkit-scrollbar-thumb {
  background-color: #d9d9d9;
  border-radius: 5px;
}

/* 滚动条轨道 */
::-webkit-scrollbar-track {
  background-color: transparent;
}
</style>
